// hooks/useInvoiceDrafts.ts
import { useCallback, useState } from "react";
import type { ProductoAgregado } from "../types/modernInvoiceForm";

type DraftId = string;

export type InvoiceDraftData = {
  // Cabecera
  tipo: "I" | "E";
  serie: string | null;

  // Cliente
  selectedCliente: {
    id: number | null;
    business_partner?: string | null;
    rfc?: string | null;
    cfdi_use?: string | null;
    tax_regime?: string | null;
    person_type?: string | number | null; // permite number
    email?: string | null;
    zip_code?: string | null; // evita 'any'
  } | null;

  // Productos
  productosAgregados: ProductoAgregado[];

  // Nota
  notaAdicional: string;

  // Relacionados
  cfdirelacionados: Array<{ tiporelacion: string; uuid: string }>;

  // Resumen (pago/moneda)
  metodoPago: string;
  formaPago: string;
  moneda: string;
  tipoCambio: number;
  diasCredito: number | "";

  // Correos
  correosExtra: string[];
};

export type InvoiceDraft = {
  id: DraftId;
  createdAt: number; // epoch ms
  title: string; // para mostrar en la UI
  data: InvoiceDraftData;
};

const LS_KEY = "invoice_drafts_v1";

function loadAll(): InvoiceDraft[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveAll(list: InvoiceDraft[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

function uuidv4() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as any).randomUUID();
  }
  // fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useInvoiceDrafts() {
  const [drafts, setDrafts] = useState<InvoiceDraft[]>(() => loadAll());

  // ⬇️ Estado REAL para el borrador activo
  const [currentDraftId, setCurrentDraftId] = useState<DraftId | null>(null);

  const refresh = useCallback(() => setDrafts(loadAll()), []);

  const saveDraft = useCallback(
    (data: InvoiceDraftData, opts?: { title?: string; id?: DraftId }) => {
      const id = opts?.id ?? uuidv4();
      const title = (opts?.title || buildDefaultTitle(data)).slice(0, 120);
      const all = loadAll();
      const idx = all.findIndex((d) => d.id === id);
      const prev = idx >= 0 ? all[idx] : null;

      const item: InvoiceDraft = {
        id,
        createdAt: prev?.createdAt ?? Date.now(), // conserva fecha si es update
        title,
        data,
      };

      if (idx >= 0) {
        all[idx] = item; // update
      } else {
        all.unshift(item); // create
      }
      saveAll(all);
      setDrafts(all);
      setCurrentDraftId(id); // ⬅️ mantener id activo en estado
      return id;
    },
    []
  );

  const removeDraft = useCallback((id: DraftId) => {
    const all = loadAll().filter((d) => d.id !== id);
    saveAll(all);
    setDrafts(all);
    setCurrentDraftId((curr) => (curr === id ? null : curr)); // ⬅️ limpia si es el activo
  }, []);

  const getDraft = useCallback((id: DraftId) => {
    return loadAll().find((d) => d.id === id) || null;
  }, []);

  return {
    list: drafts,
    saveDraft,
    removeDraft,
    getDraft,
    currentDraftId, // siempre consistente
    setCurrentDraftId, // setter real
    refresh,
  };
}

// Un título útil para la lista
function buildDefaultTitle(data: InvoiceDraftData) {
  const cliente = data?.selectedCliente?.business_partner || "Sin cliente";
  const nConceptos = data?.productosAgregados?.length || 0;
  const serie = data?.serie || "—";
  const tipo = data?.tipo === "E" ? "EGRESO" : "INGRESO";
  return `${tipo} • Serie ${serie} • ${cliente} • ${nConceptos} concepto(s)`;
}
